import { signOut } from 'actions/auth'
import { fetchCredits } from 'actions/creditBalance'
import { getSeattleDriverStatus } from 'actions/seattle'

import { TRAINING_CERTS } from 'util/constants'
import { getVehicles, patchDecodePlate } from './vehicles'
import * as mainApi from 'util/server/mainApi'
import { driverSchedulingApi } from 'util/server/driverSchedulingApi'
import { optimizely } from 'Optimizely'
import { hasOngoingAssignedBlock } from 'util/assignedBlocks'

function identifyFullStory(profileId) {
  if (window.FS) {
    window.FS.identify(profileId)
  }
}

export function getProfile() {
  return (dispatch) => {
    console.log('Loading profile', new Date())
    return mainApi
      .getProfile()
      .then(async (resp) => {
        const { profile } = resp.data
        const lockReason = profile.locked && profile.active_locks ? profile.active_locks[0] : null
        await Promise.allSettled([
          dispatch(fetchCredits()),
          dispatch(getAuthenticityVerification()),
          dispatch(getEarnings()),
          dispatch(getSeattleDriverStatus(profile.id)),
          dispatch(getVehicles(profile.id)),
          dispatch(getTermsDocuments()),
          dispatch(getDisclosureDocuments()),
          dispatch(getProfileSettings(profile.id)),
          dispatch(getProfileFlags(profile.id)),
          dispatch(fetchDriverCerts()),
          dispatch({ type: 'SET_IS_LOCKED', isLocked: profile.locked }),
          dispatch({ type: 'SET_LOCK_REASON', lockReason: lockReason }),
          identifyFullStory(profile.id),
        ])
        console.log('Loading profile complete', new Date())
        return dispatch({ type: 'SET_PROFILE', profile })
      })
      .catch((err) => {
        let message
        if (err.response && err.response.status === 401) {
          message = `You are no longer signed in.`
        }
        dispatch(signOut({ message }))
      })
  }
}

export function seenMessage(id) {
  return (dispatch) => {
    // TODO: Notify backend of seen
    return dispatch({ type: 'SEEN_MESSAGE', id })
  }
}

export function verifyPhone(code) {
  return (dispatch) => {
    return mainApi.createPhoneConfirmation(code).then(() => {
      return dispatch(getProfile())
    })
  }
}

export function updatePhoneNumber(phone_number) {
  return (dispatch) => {
    return mainApi.updatePhoneNumber(phone_number).then(() => {
      return dispatch(getProfile())
    })
  }
}

export function updateProfile(data) {
  return (dispatch) => {
    return mainApi.updateProfile(data).then(() => {
      return dispatch(getProfile())
    })
  }
}

export function addBusinessInfo(business_name, state, ein, multi_member_ein) {
  return (dispatch) => {
    return mainApi.createBusinessInfo(business_name, state, ein, multi_member_ein).finally(() => {
      return dispatch(getProfile())
    })
    // Finally because if the request fails, we still want to refresh the profile to get the potential active_lock reason
  }
}

export function deleteBusinessInfo(id) {
  return (dispatch) => {
    return mainApi.deleteBusinessInfo(id).then(() => {
      return dispatch(getProfile())
    })
  }
}

export function getTermsDocuments() {
  return (dispatch) => {
    return mainApi.getTermsDocuments().then((resp) => {
      return dispatch({ type: 'SET_TERMS_DOCUMENTS', termsDocuments: resp.data })
    })
  }
}

export function getDisclosureDocuments() {
  return (dispatch) => {
    return mainApi.getDisclosureDocuments().then((resp) => {
      return dispatch({ type: 'SET_DISCLOSURE_DOCUMENTS', disclosureDocuments: resp.data })
    })
  }
}

export function getProfileSettings(profile_id) {
  return (dispatch) => {
    return mainApi.getProfileSettings(profile_id).then(async (resp) => {
      const settings = resp.data.settings
      return dispatch({ type: 'SET_ACCOUNT_SETTINGS', settings })
    })
  }
}

export function getProfileFlags(profile_id) {
  return (dispatch) => {
    return mainApi
      .getProfileFlags(profile_id)
      .then(async (resp) => {
        const flags = resp.data.flags
        return dispatch({ type: 'SET_FLAGS', flags })
      })
      .catch(() => {
        return dispatch({ type: 'SET_FLAGS', flags: [{}] })
      })
  }
}

export function updateDriversLicense(data) {
  return (dispatch) => {
    return mainApi.createDriversLicense(data).then(() => {
      return dispatch(getProfile())
    })
  }
}

export function addDebitCard({ stripe_token, last4, expiration_month, expiration_year }) {
  return (dispatch) => {
    return mainApi
      .createDebitAccount({
        stripe_token,
        last4,
        expiration_month,
        expiration_year,
      })
      .then(() => {
        return dispatch(getProfile())
      })
      .catch((error) => {
        throw error
      })
  }
}

export function addBankAccount({
  routing_number,
  account_number,
  account_type = 'checking',
  account_holder_type = 'personal',
}) {
  return (dispatch) => {
    return mainApi
      .createBankAccount({
        routing_number,
        account_number,
        account_type,
        account_holder_type,
      })
      .then(() => {
        return dispatch(getProfile())
      })
  }
}

export function updateBankAccount(
  id,
  { routing_number, account_number, account_type = 'checking', account_holder_type = 'personal' }
) {
  return (dispatch) => {
    return mainApi
      .updateBankAccount(id, {
        routing_number,
        account_number,
        account_type,
        account_holder_type,
      })
      .then(() => {
        return dispatch(getProfile())
      })
  }
}

export function verifyBankAccount(id, deposits) {
  return (dispatch) => {
    return mainApi.updateBankAccountVerification(id, deposits).then(() => {
      return dispatch(getProfile())
    })
  }
}

export function saveCreditCard(data, silenceDispatch = false) {
  return (dispatch) => {
    const { payment_provider, ...params } = data

    let payload

    if (payment_provider === 'stripe') {
      try {
        console.log(params.stripe_card)

        payload = {
          credit_card: {
            stripe_token: params.stripe_card.id,
            account_number: params.stripe_card.card.last4,
            expiration_month: String(params.stripe_card.card.exp_month),
            expiration_year: String(params.stripe_card.card.exp_year),
          },
        }
      } catch (er) {
        console.log('er', er)
        throw er
      }
    } else {
      const [expiration_month, expiration_year] = params.credit_card.expiration_date.split('/').map(Number)
      const credit_card = Object.assign({}, params.credit_card, { expiration_month, expiration_year })
      delete credit_card.expiration_date
      credit_card.account_number = credit_card.account_number.replace(/\s/g, '')
      payload = { credit_card }
    }

    return mainApi.createCreditCard(payload).then(() => {
      if (!silenceDispatch) {
        return dispatch(getProfile())
      }
    })
  }
}

export function updatePhoto(file) {
  return (dispatch) => {
    return mainApi.updateAvatar(file).then(() => {
      return dispatch(getProfile())
    })
  }
}

export function completeDriverOnboardingModule(name, profileId, vehicles) {
  const vehicle = vehicles[0] // Take first (hopefully only) vehicle in new driver's vehicle list
  const plateLookupFF = optimizely.decide('drive_license_plate_registration_lookup')
  const vehicleHasLicensePlateAndState = vehicle?.details?.license_plate && vehicle?.details?.license_plate_state

  return (dispatch) => {
    const promise = mainApi.createCertification(name)
    if (name == TRAINING_CERTS.ONBOARDING && plateLookupFF.enabled && vehicleHasLicensePlateAndState) {
      dispatch(
        patchDecodePlate(
          profileId,
          vehicle.details.id,
          vehicle.details.license_plate,
          vehicle.details.license_plate_state
        )
      )
    }

    return promise
  }
}

export function addCert(type, options = {}) {
  const { refetchProfile = true } = options
  return (dispatch) => {
    const promise = mainApi.createCertification(type)

    if (refetchProfile) {
      return promise.then(() => {
        return dispatch(getProfile())
      })
    }

    return promise
  }
}

export function addDriverCert(name) {
  // WEB-2396 change to add both certs for roadie_xd can be removed eventually
  if (name == TRAINING_CERTS.ROADIE_XD.CERT) {
    return (dispatch) => {
      return Promise.all([
        mainApi.createDriverCertification(name),
        mainApi.createDriverCertification(TRAINING_CERTS.ROADIE_XD_BETA.CERT),
      ]).then(() => dispatch(fetchDriverCerts()))
    }
  } else {
    return (dispatch) => {
      return mainApi.createDriverCertification(name).then(() => dispatch(fetchDriverCerts()))
    }
  }
}

export function deleteDriverCert(name) {
  return (dispatch) => {
    return mainApi.deleteDriverCertification(name).then(() => dispatch(fetchDriverCerts()))
  }
}

export function fetchDriverCerts() {
  return async (dispatch) => {
    try {
      const res = await mainApi.getDriverCertifications()
      const certifications = res.data ?? { delivery_certifications_drivers: [] }

      dispatch(saveTrainingCertificationsToStore(certifications))
      return certifications
    } catch (error) {
      console.warn('Failed to fetch driver certifications', error)

      const certifications = { delivery_certifications_drivers: [] }
      dispatch(saveTrainingCertificationsToStore(certifications))
      return certifications
    }
  }
}

export function saveTrainingCertificationsToStore(data) {
  return (dispatch) => {
    return dispatch({ type: 'SET_TRAINING_CERTS', data })
  }
}

export function getAuthenticityVerification() {
  return (dispatch) => {
    return mainApi.getAuthenticityVerification().then((res) => {
      dispatch({ type: 'SET_AUTHENTICITY_VERIFICATION', state: res.data.state })
    })
  }
}

export function getEarnings() {
  return (dispatch) => {
    return mainApi.getEarningsStatements().then((res) => {
      const earningsStatements = res.data.earnings_statements ?? []

      const prop22EarningsStatements = earningsStatements.filter((statement) => statement.type === 'prop22_bi_weekly')
      const seattleEarningsStatements = earningsStatements.filter((statement) => statement.type === 'seattle_weekly')
      const nycEarningsStatements = earningsStatements.filter((statement) => statement.type === 'new_york_city_weekly')

      dispatch({ type: 'SET_EARNINGS_STATEMENT', earningsStatement: prop22EarningsStatements })
      dispatch({ type: 'SET_SEATTLE_EARNINGS_STATEMENT', earningsStatement: seattleEarningsStatements })
      dispatch({ type: 'SET_NYC_EARNINGS_STATEMENT', earningsStatement: nycEarningsStatements })
    })
  }
}

export function setMarketingCommOptIn(profile_id, opt_in) {
  return (dispatch) => {
    return mainApi.updateMarketingCommOptIn(profile_id, opt_in).then(async () => {
      dispatch(getProfileSettings(profile_id))
    })
  }
}

export function getDriverRoute() {
  return (dispatch) => {
    return mainApi
      .getDriverRoute()
      .then((res) => {
        dispatch({ type: 'SET_DRIVER_ROUTE', driverRoute: res.data })
      })
      .catch(() => {
        dispatch({ type: 'SET_DRIVER_ROUTE', driverRoute: null })
      })
  }
}

export function getDriverAssignedBlocks() {
  return (dispatch) => {
    dispatch({ type: 'SET_DRIVER_ACTIVE_BLOCK_STATUS', status: 'loading' })

    return Promise.resolve()
      .then(() => driverSchedulingApi.get('/driver/assigned_blocks'))
      .then((res) => {
        if (!Array.isArray(res.data)) {
          throw new Error('Unexpected /driver/assigned_blocks response')
        }

        dispatch({ type: 'SET_DRIVER_HAS_ACTIVE_BLOCK', hasActiveBlock: hasOngoingAssignedBlock(res.data) })
        dispatch({ type: 'SET_DRIVER_ACTIVE_BLOCK_STATUS', status: 'loaded' })
        dispatch({ type: 'CLEAR_FLASH', id: 'active-block-status-error' })
      })
      .catch(() => {
        dispatch({ type: 'SET_DRIVER_HAS_ACTIVE_BLOCK', hasActiveBlock: false })
        dispatch({ type: 'SET_DRIVER_ACTIVE_BLOCK_STATUS', status: 'loaded' })
        dispatch({ type: 'CLEAR_FLASH', id: 'active-block-status-error' })
      })
  }
}
